import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, Paperclip, Search, Send } from 'lucide-react'
import { ApiError, apiFetch, qs } from '../lib/api'
import { API_BASE_URL } from '../lib/config'
import { useDebounced } from '../lib/hooks'
import type {
	SupportAttachment,
	SupportMessage,
	SupportStatus,
	SupportThread,
	SupportThreadList,
	SupportThreadRow,
} from '../lib/types'
import { DataTable } from '../components/data-table'
import { CursorPagination } from '../components/pagination'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge, StatusBadge, type Tone } from '../components/badge'
import { useToast } from '../components/toast'

const STATUS: Record<SupportStatus, { label: string; tone: Tone }> = {
	open: { label: 'Open', tone: 'info' },
	awaiting: { label: 'Awaiting reply', tone: 'warning' },
	replied: { label: 'Replied', tone: 'neutral' },
	done: { label: 'Done', tone: 'success' },
}

// 'all' includes what Gmail filed as spam, flagged with a badge.
type Folder = 'all' | 'inbox' | 'spam'

const columns: ColumnDef<SupportThreadRow, any>[] = [
	{
		header: 'From',
		cell: ({ row }) => (
			<div className="min-w-0 max-w-[14rem]">
				<p className="truncate font-medium">{row.original.from.name || row.original.from.email}</p>
				{row.original.from.name && <p className="truncate text-xs text-ink-soft">{row.original.from.email}</p>}
			</div>
		),
	},
	{
		header: 'Subject',
		cell: ({ row }) => (
			<div className="min-w-0 max-w-xl">
				<p className="truncate">
					{row.original.spam && (
						<span className="mr-1.5 align-middle">
							<Badge tone="warning">Spam</Badge>
						</span>
					)}
					{row.original.subject}
					{row.original.messages > 1 && <span className="ml-1.5 text-xs text-ink-soft">({row.original.messages})</span>}
				</p>
				<p className="truncate text-xs text-ink-soft">{row.original.snippet}</p>
			</div>
		),
	},
	{
		header: 'Status',
		cell: ({ row }) =>
			// A customer writing again after a reply or Done makes it Awaiting
			// reply (backend), so Open + Awaiting reply = everything to answer.
			row.original.status ? (
				<Badge tone={STATUS[row.original.status].tone}>{STATUS[row.original.status].label}</Badge>
			) : (
				<span className="text-ink-faint">—</span>
			),
	},
	{
		header: 'Last message',
		accessorKey: 'lastAt',
		cell: ({ getValue }) => <span className="whitespace-nowrap">{formatDate(getValue<string>())}</span>,
	},
]

export function SupportInboxPage() {
	const [search, setSearch] = useState('')
	const [folder, setFolder] = useState<Folder>('all')
	const [status, setStatus] = useState<SupportStatus | ''>('')
	// Gmail pages with an opaque token, so going back means remembering where we
	// have been — the same as the Chargebee-backed coupons list.
	const [cursors, setCursors] = useState<string[]>([])
	const [selected, setSelected] = useState<string | null>(null)
	const q = useDebounced(search)
	const cursor = cursors[cursors.length - 1]

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['support-threads', q, folder, status, cursor],
		queryFn: () => apiFetch<SupportThreadList>(`/api/admin/support/threads${qs({ q, folder, status, cursor })}`),
	})

	return (
		<>
			<PageHeader
				title="Support inbox"
				subtitle="Every email sent to support@cloudbasedbackup.com — live from Gmail."
			/>

			<div className="card">
				<div className="flex flex-wrap gap-2 border-b border-line p-3">
					<div className="relative min-w-0 flex-1 sm:max-w-xs">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
						<input
							className="input pl-9"
							placeholder="Search like in Gmail (email, word…)"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value)
								setCursors([])
							}}
						/>
					</div>
					<select
						className="input w-auto"
						value={folder}
						onChange={(e) => {
							setFolder(e.target.value as Folder)
							setCursors([])
						}}
					>
						<option value="all">All emails</option>
						<option value="inbox">Without spam</option>
						<option value="spam">Spam only</option>
					</select>
					<select
						className="input w-auto"
						value={status}
						onChange={(e) => {
							setStatus(e.target.value as SupportStatus | '')
							setCursors([])
						}}
					>
						<option value="">Any status</option>
						<option value="open">Open</option>
						<option value="awaiting">Awaiting reply</option>
						<option value="replied">Replied</option>
						<option value="done">Done</option>
					</select>
				</div>

				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<DataTable
							columns={columns}
							rows={data.rows}
							emptyMessage={search ? 'No emails match that search.' : 'No support emails yet.'}
							onRowClick={(row) => setSelected(row.id)}
						/>
						<CursorPagination
							cursors={cursors}
							nextCursor={data.nextCursor}
							onBack={() => setCursors((c) => c.slice(0, -1))}
							onNext={() => data.nextCursor && setCursors((c) => [...c, data.nextCursor!])}
						/>
					</>
				)}
			</div>

			{selected && <ThreadDetail id={selected} onClose={() => setSelected(null)} />}
		</>
	)
}

function ThreadDetail({ id, onClose }: { id: string; onClose: () => void }) {
	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['support-thread', id],
		queryFn: () => apiFetch<SupportThread>(`/api/admin/support/threads/${encodeURIComponent(id)}`),
	})

	return (
		<div className="fixed inset-0 z-50 flex justify-end">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={onClose} />
			<aside role="dialog" aria-modal className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-surface p-5">
				{/* Stays put while the messages scroll. Sticky offsets are measured
				    inside the panel's padding, so -top-5 (= -p-5) pins it to the very
				    top edge; the negative margins let it cover that padding so text
				    passes behind it, not through a gap above it. */}
				<div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-4 border-b border-line bg-surface px-5 pb-3 pt-5">
					<div className="flex items-start justify-between gap-3">
						<h2 className="min-w-0 break-words font-display text-lg font-semibold">{data?.subject ?? 'Conversation'}</h2>
						<div className="flex shrink-0 items-center gap-2">
							{data && <StatusSelect threadId={id} status={data.status} />}
							<button type="button" className="btn-secondary" onClick={onClose}>
								Close
							</button>
						</div>
					</div>
					{data && <SenderCard thread={data} />}
				</div>

				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<div className="flex flex-col gap-4">
							{data.messages.map((m) => (
								<MessageCard key={m.id} message={m} />
							))}
						</div>
						{data.sender && <ReplyBox threadId={id} to={data.sender} subject={data.subject} />}
					</>
				)}
			</aside>
		</div>
	)
}

// Open / Replied / Done, saved as a Gmail label on the conversation. Sending a
// reply sets Replied automatically.
function StatusSelect({ threadId, status }: { threadId: string; status: SupportStatus | null }) {
	const queryClient = useQueryClient()
	const toast = useToast()
	const change = useMutation({
		mutationFn: (next: SupportStatus) =>
			apiFetch<{ ok: true; status: SupportStatus }>(`/api/admin/support/threads/${encodeURIComponent(threadId)}/status`, {
				method: 'PATCH',
				body: { status: next },
			}),
		onSuccess: (result) => {
			toast(`Marked as ${STATUS[result.status].label.toLowerCase()}.`)
			queryClient.invalidateQueries({ queryKey: ['support-thread', threadId] })
			queryClient.invalidateQueries({ queryKey: ['support-threads'] })
		},
		onError: (err) => toast(err.message, 'error'),
	})

	return (
		<select
			aria-label="Status"
			className="input w-auto py-1.5"
			value={change.isPending ? change.variables : (status ?? '')}
			disabled={change.isPending}
			onChange={(e) => change.mutate(e.target.value as SupportStatus)}
		>
			{/* Unknown until the Gmail connection may set labels. */}
			{!status && <option value="">Status…</option>}
			<option value="open">Open</option>
			<option value="awaiting">Awaiting reply</option>
			<option value="replied">Replied</option>
			<option value="done">Done</option>
		</select>
	)
}

function SenderCard({ thread }: { thread: SupportThread }) {
	if (!thread.sender) return null
	const cb = thread.chargebee
	return (
		<div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 text-sm">
			<span className="truncate font-medium">{thread.sender}</span>
			{cb === null ? (
				<span className="text-xs text-ink-faint">Chargebee lookup failed</span>
			) : cb.subscriptions.length ? (
				cb.subscriptions.map((s) => (
					<a key={s.id} href={s.url ?? undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">
						<StatusBadge value={s.status} />
						<span className="text-xs text-ink-soft">{s.plan ?? s.id}</span>
						{s.url && <ExternalLink className="h-3 w-3 text-ink-faint" />}
					</a>
				))
			) : cb.customers.length ? (
				<Badge tone="neutral">Chargebee customer, no subscription</Badge>
			) : (
				<Badge tone="neutral">Not a customer</Badge>
			)}
		</div>
	)
}

// Sent by the backend through Gmail as support@, in this conversation, with
// the group in Cc — so it shows up here, in Google Groups and for the customer
// as a normal reply. The previous message is quoted below the text.
function ReplyBox({ threadId, to, subject: threadSubject }: { threadId: string; to: string; subject: string }) {
	const queryClient = useQueryClient()
	const toast = useToast()
	const defaultSubject = /^re:/i.test(threadSubject) ? threadSubject : `Re: ${threadSubject}`
	const [subject, setSubject] = useState(defaultSubject)
	const [text, setText] = useState('')
	const changed = subject.trim() !== '' && subject.trim() !== defaultSubject

	const send = useMutation({
		mutationFn: () =>
			apiFetch<{ ok: true; to: string; newConversation: boolean }>(
				`/api/admin/support/threads/${encodeURIComponent(threadId)}/reply`,
				{ method: 'POST', body: { text, subject: subject.trim() || defaultSubject } },
			),
		// The draft is kept on failure so nothing typed is lost.
		onError: (err) => toast(err.message, 'error'),
		onSuccess: (result) => {
			setText('')
			setSubject(defaultSubject)
			toast(`Reply sent to ${result.to}${result.newConversation ? ' as a new conversation' : ''}.`)
			queryClient.invalidateQueries({ queryKey: ['support-thread', threadId] })
			queryClient.invalidateQueries({ queryKey: ['support-threads'] })
		},
	})

	return (
		<form
			className="mt-4 rounded-lg border border-line"
			onSubmit={(e) => {
				e.preventDefault()
				if (text.trim() && !send.isPending) send.mutate()
			}}
		>
			<p className="border-b border-line px-3 py-2 text-xs text-ink-soft">
				Reply to <span className="font-medium text-ink">{to}</span> as support@cloudbasedbackup.com
			</p>
			<label className="flex items-center gap-2 border-b border-line px-3 py-2 text-sm">
				<span className="text-xs text-ink-soft">Subject</span>
				<input
					className="min-w-0 flex-1 bg-transparent outline-none"
					value={subject}
					onChange={(e) => {
						setSubject(e.target.value)
					}}
				/>
			</label>
			{changed && (
				<p className="border-b border-line bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
					A new subject starts a new conversation — it gets its own row, and the customer sees it as a separate email.
				</p>
			)}
			<textarea
				className="block min-h-32 w-full resize-y bg-transparent px-3 py-2 text-sm outline-none"
				placeholder="Write your reply…"
				value={text}
				onChange={(e) => {
					setText(e.target.value)
				}}
			/>
			<div className="flex flex-wrap items-center justify-end gap-3 border-t border-line px-3 py-2">
				<button type="submit" className="btn-primary" disabled={!text.trim() || send.isPending}>
					<Send className="h-4 w-4" />
					{send.isPending ? 'Sending…' : 'Send reply'}
				</button>
			</div>
		</form>
	)
}

function MessageCard({ message }: { message: SupportMessage }) {
	// Formatted (HTML) by default, as the customer wrote it; plain text is the
	// fallback for emails without HTML, and one click away for the rest.
	const [formatted, setFormatted] = useState(Boolean(message.html))
	return (
		<article className="rounded-lg border border-line">
			<header className="flex flex-wrap items-start justify-between gap-2 border-b border-line px-3 py-2">
				<div className="min-w-0 text-sm">
					<p className="truncate font-medium">
						{message.from.name || message.from.email}
						{message.from.name && <span className="ml-1.5 font-normal text-ink-soft">&lt;{message.from.email}&gt;</span>}
					</p>
					<p className="truncate text-xs text-ink-soft">
						To {message.to}
						{message.cc && ` · Cc ${message.cc}`}
					</p>
				</div>
				<span className="whitespace-nowrap text-xs text-ink-soft">{formatDate(message.date)}</span>
			</header>

			{formatted && message.html ? (
				<SafeHtml html={message.html} />
			) : (
				<PlainText text={message.text} images={message.inlineImages} />
			)}

			{(message.attachments.length > 0 || (message.text && message.html)) && (
				<footer className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-2 text-xs text-ink-soft">
					{message.attachments.map((a) => (
						<AttachmentLink key={a.partId} messageId={message.id} attachment={a} />
					))}
					{message.text && message.html && (
						<button type="button" className="ml-auto underline" onClick={() => setFormatted((f) => !f)}>
							{formatted ? 'Show plain text' : 'Show formatted'}
						</button>
					)}
				</footer>
			)}
		</article>
	)
}

// Where the earlier emails that a mail app copied into a reply begin: an
// "On … wrote:" line (English, German, French; it may wrap over a few lines),
// an Outlook "Original Message" divider, or else the first "> " quoted line.
const QUOTE_START = [
	/\n(?:On|Am|Le)\s[^\n]*(?:\n[^\n]*){0,2}?(?:wrote|schrieb|a écrit)\s?:\s*(?:\n|$)/i,
	/\n-{2,}\s*(?:Original Message|Ursprüngliche Nachricht|Message d'origine)\s*-{2,}/i,
	/\n_{10,}\s*\n(?:From|Von|De)\s?:/,
	/\n>/,
]

function splitQuoted(text: string): [string, string] {
	const cuts = QUOTE_START.map((re) => text.search(re)).filter((i) => i > 0)
	if (!cuts.length) return [text, '']
	const at = Math.min(...cuts)
	return [text.slice(0, at).trimEnd(), text.slice(at).trim()]
}

// Shows only what is new in this message, like Gmail; the copied history is
// one click away (it is usually also shown as earlier messages above).
function PlainText({ text, images }: { text: string; images: Record<string, string> }) {
	const [showQuoted, setShowQuoted] = useState(false)
	const [fresh, quoted] = splitQuoted(text || '')
	return (
		<div className="px-3 py-3 text-sm">
			<p className="whitespace-pre-wrap break-words">
				{fresh ? <WithImages text={fresh} images={images} /> : quoted ? '' : '(empty message)'}
			</p>
			{quoted && (
				<>
					<button
						type="button"
						className="mt-2 rounded border border-line px-2 text-xs text-ink-soft hover:bg-canvas"
						onClick={() => setShowQuoted((v) => !v)}
						aria-expanded={showQuoted}
					>
						{showQuoted ? 'Hide quoted text' : '••• Show quoted text'}
					</button>
					{showQuoted && (
						<p className="mt-2 whitespace-pre-wrap break-words text-ink-soft">
							<WithImages text={quoted} images={images} />
						</p>
					)}
				</>
			)}
		</div>
	)
}

function AttachmentLink({ messageId, attachment }: { messageId: string; attachment: SupportAttachment }) {
	const toast = useToast()
	const [loading, setLoading] = useState(false)
	return (
		<button
			type="button"
			className="inline-flex items-center gap-1 underline disabled:opacity-60"
			disabled={loading}
			title={`Download ${attachment.name} (${formatSize(attachment.size)})`}
			onClick={() => {
				setLoading(true)
				downloadAttachment(messageId, attachment)
					.catch((err: unknown) =>
						toast(`Could not download ${attachment.name}: ${err instanceof Error ? err.message : 'unknown error'}`, 'error'),
					)
					.finally(() => setLoading(false))
			}}
		>
			<Paperclip className="h-3 w-3" />
			{attachment.name}
			<span className="no-underline text-ink-faint">({formatSize(attachment.size)})</span>
		</button>
	)
}

const formatSize = (bytes: number) =>
	bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

// Mail apps put "[cid:…]" in the plain-text version where an image was pasted
// into the email; show the image there instead.
function WithImages({ text, images }: { text: string; images: Record<string, string> }) {
	const pieces = text.split(/\[cid:([^\]]+)\]/)
	return (
		<>
			{pieces.map((piece, i) =>
				i % 2 === 0 ? (
					piece
				) : images[piece] ? (
					<img key={i} src={images[piece]} alt="" className="my-2 block max-w-full rounded border border-line" />
				) : (
					`[image]`
				),
			)}
		</>
	)
}

// Attachments come from the backend with the admin session cookie, so they are
// fetched and handed to the browser as a file rather than linked directly.
async function downloadAttachment(messageId: string, a: SupportAttachment) {
	const res = await fetch(
		`${API_BASE_URL}/api/admin/support/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(a.partId)}`,
		{ credentials: 'include' },
	)
	if (!res.ok) {
		const data = (await res.json().catch(() => ({}))) as { error?: string }
		throw new ApiError(res.status, data.error ?? 'Download failed')
	}
	const url = URL.createObjectURL(await res.blob())
	const link = document.createElement('a')
	link.href = url
	link.download = a.name
	link.click()
	setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

// Email HTML is untrusted: it is rendered in a sandbox (its own opaque
// origin, no access to this page) with remote images blocked (they would tell
// the sender the email was opened). The frame grows to the email's height so
// the panel scrolls as one, like plain text: a single script — allowed only by
// a per-render nonce, so scripts inside the email still cannot run — reports
// the content height to this page.
function SafeHtml({ html }: { html: string }) {
	const frame = useRef<HTMLIFrameElement>(null)
	const [height, setHeight] = useState(160)
	const nonce = useMemo(() => crypto.randomUUID().replace(/-/g, ''), [])

	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (e.source !== frame.current?.contentWindow) return
			const h = Number((e.data as { emailHeight?: unknown })?.emailHeight)
			if (Number.isFinite(h) && h > 0) setHeight(Math.min(Math.ceil(h), 20000))
		}
		window.addEventListener('message', onMessage)
		return () => window.removeEventListener('message', onMessage)
	}, [])

	const doc = `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'nonce-${nonce}'">
<base target="_blank"><style>html{overflow-y:hidden}body{font:14px/1.5 system-ui,sans-serif;margin:12px;color:#111;word-wrap:break-word;overflow-x:auto}img{max-width:100%;height:auto}</style>
</head><body>${html}<script nonce="${nonce}">(function(){var post=function(){parent.postMessage({emailHeight:document.documentElement.scrollHeight},'*')};new ResizeObserver(post).observe(document.body);addEventListener('load',post);post()})()</script></body></html>`

	return (
		<iframe
			ref={frame}
			title="Email content"
			sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
			srcDoc={doc}
			style={{ height }}
			className="block w-full bg-white"
		/>
	)
}
